import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Check, Copy, DownloadCloud, Trash2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

interface FileActionsProps {
    fileId: string;
    fileUrl: string;
    fileName: string;
    onDelete: (id: string) => void;
    copiedLinkId: string | null;
    onCopyLink: (id: string, url: string) => void;
    variant?: "dropdown" | "buttons";
    className:any;
}

const FileActions = ({
    fileId,
    fileUrl,
    fileName,
    onDelete,
    copiedLinkId,
    onCopyLink,
    variant = "dropdown",
    className =''
}: FileActionsProps) => {
    const { toast } = useToast();

    const handleDownload = async (fileUrl: string, name: string) => {

        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link: any = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name); // Provide a name for the file  
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        toast({
            title: "Downloading file",
            description: `${name} is being downloaded.`,
        });
    };

    if (variant === "dropdown") {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onCopyLink(fileId, fileUrl)}>
                        {copiedLinkId === fileId ? (
                            <>
                                <Check className="mr-2 h-4 w-4 text-green-500" />
                                Copied
                            </>
                        ) : (
                            <>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy Link
                            </>
                        )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownload(fileUrl, fileName)}>
                        <DownloadCloud className="mr-2 h-4 w-4" />
                        Download
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(fileId)}>
                        <Trash2 className="mr-2 h-4 w-4 text-red-500" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <div className="flex justify-end gap-2">
            <Button
                variant="ghost"
                size="sm"
                onClick={() => onCopyLink(fileId, fileUrl)}
            >
                {copiedLinkId === fileId ? (
                    <>
                        <Check className="mr-1 h-4 w-4 text-green-500" />
                        Copied
                    </>
                ) : (
                    <>
                        <Copy className="mr-1 h-4 w-4" />
                        Copy Link
                    </>
                )}
            </Button>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(fileUrl, fileName)}
            >
                <DownloadCloud className="mr-1 h-4 w-4" />
                Download
            </Button>
            <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => onDelete(fileName)}
            >
                <Trash2 className="mr-1 h-4 w-4" />
                Delete
            </Button>
        </div>
    );
};

export default FileActions;
